import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '../hooks/useAuthContext'
import Navbar from '../components/Navbar'
import AdminSectionLayout from '../components/AdminSectionLayout'
import { axiosInstance } from '../context/AuthContext'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa'

const AdminBlogPage = () => {
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const [blogs, setBlogs] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingBlog, setEditingBlog] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const itemsPerPage = 10

  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [status, setStatus] = useState(0)
  const [tags, setTags] = useState('')
  const [featuredImage, setFeaturedImage] = useState('')
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (user && (user.permissions.includes('admin') || user.permissions.includes('artist'))) {
      fetchBlogs()
    }
  }, [user, page, searchQuery, statusFilter])

  useEffect(() => {
    if (user && !user.permissions.includes('admin') && !user.permissions.includes('artist')) {
      navigate('/home')
    }
  }, [user, navigate])

  const fetchBlogs = async () => {
    try {
      setError('')
      setIsLoading(true)
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString(),
      })
      if (searchQuery) params.append('search', searchQuery)
      if (statusFilter) params.append('status', statusFilter)

      const response = await axiosInstance.get(`/blog?${params.toString()}`)
      setBlogs(response.data.blogs || [])
      setTotalPages(response.data.totalPages || 1)
    } catch (error) {
      console.error('Error fetching blogs:', error)
      setError('Failed to load blogs')
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenModal = (blog = null) => {
    if (blog) {
      setEditingBlog(blog)
      setTitle(blog.title || '')
      setContent(blog.content || '')
      setExcerpt(blog.excerpt || '')
      setStatus(blog.status || 0)
      setTags((blog.tags || []).join(', '))
      setFeaturedImage(blog.featuredImage || '')
      setImagePreview(blog.featuredImage || '')
      setImageFile(null)
    } else {
      setEditingBlog(null)
      setTitle('')
      setContent('')
      setExcerpt('')
      setStatus(0)
      setTags('')
      setFeaturedImage('')
      setImagePreview('')
      setImageFile(null)
    }
    setError('')
    setMessage('')
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingBlog(null)
    setError('')
    setMessage('')
  }

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'image'],
      ['clean']
    ]
  }

  const hasContent = (htmlContent) => {
    if (!htmlContent) return false
    const textContent = htmlContent.replace(/<[^>]*>/g, '').trim()
    return textContent.length > 0
  }

  const handleImageUpload = (e) => {
    const selectedImage = e.target.files[0]
    if (selectedImage) {
      setImageFile(selectedImage)
      
      // Convert to base64
      const reader = new FileReader()
      reader.onload = () => {
        const base64String = reader.result
        setFeaturedImage(base64String)
        setImagePreview(base64String)
      }
      reader.onerror = () => {
        setError('Failed to read image file')
      }
      reader.readAsDataURL(selectedImage)
    }
  }

  const handleImageUrlChange = (e) => {
    const url = e.target.value
    setFeaturedImage(url)
    setImagePreview(url)
    setImageFile(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setMessage('')

    if (!title.trim() || !hasContent(content)) {
      setError('Title and content are required')
      return
    }

    if (!featuredImage || !featuredImage.trim()) {
      setError('Featured image is required')
      return
    }

    try {
      setIsSubmitting(true)
      const textContent = content.replace(/<[^>]*>/g, '').trim()
      const blogData = {
        title: title.trim(),
        content: content,
        excerpt: excerpt.trim() || textContent.substring(0, 200),
        status: typeof status === 'number' ? status : Number(status),
        tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0),
        featuredImage: featuredImage.trim(),
      }

      if (editingBlog) {
        await axiosInstance.patch(`/blog/${editingBlog._id}`, blogData)
        setMessage('Blog updated successfully!')
      } else {
        await axiosInstance.post('/blog', blogData)
        setMessage('Blog created successfully!')
      }

      setTimeout(() => {
        handleCloseModal()
        fetchBlogs()
      }, 1000)
    } catch (error) {
      console.error('Error saving blog:', error)
      setError(error.response?.data?.error || 'Failed to save blog')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return
    }

    try {
      await axiosInstance.delete(`/blog/${blogId}`)
      setMessage('Blog deleted successfully!')
      fetchBlogs()
      setTimeout(() => setMessage(''), 3000)
    } catch (error) {
      console.error('Error deleting blog:', error)
      setError(error.response?.data?.error || 'Failed to delete blog')
      setTimeout(() => setError(''), 3000)
    }
  }

  const handlePublishToggle = async (blog) => {
    try {
      await axiosInstance.patch(`/blog/${blog._id}`, {
        status: blog.status === 1 ? 0 : 1,
      })
      fetchBlogs()
    } catch (error) {
      console.error('Error updating blog status:', error)
      setError(error.response?.data?.error || 'Failed to update blog status')
      setTimeout(() => setError(''), 3000)
    }
  }

  if (!user) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-4">Blog Management</h1>
        <p>Please log in to manage your blog posts.</p>
      </div>
    )
  }

  return (
    <>
      <Navbar />
      <AdminSectionLayout
        title="Blogs"
        description="Create, edit, and manage your blog posts"
        actions={
          <button
            onClick={() => handleOpenModal()}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition flex items-center justify-center gap-2 text-sm"
          >
            <FaPlus />
            New Blog Post
          </button>
        }
      >
        <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
          <input
            type="text"
            placeholder="Search blogs..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPage(1)
            }}
            className="w-full sm:flex-1 sm:min-w-[200px] rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
            className="w-full sm:w-auto rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="0">Draft</option>
            <option value="1">Published</option>
          </select>
        </div>

        {message && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">{message}</div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">{error}</div>
        )}

        {isLoading ? (
          <div className="text-center py-12">Loading blogs...</div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg mb-2">No blog posts found</p>
            <p>Create your first blog post to get started!</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {blogs.map((blog) => (
                <div
                  key={blog._id}
                  className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:justify-between lg:items-start mb-3">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                        <h3 
                          onClick={() => navigate(`/blog/${blog._id}`)}
                          className="text-lg sm:text-xl font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors break-words"
                        >
                          {blog.title}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded ${
                            blog.status === 1
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {blog.status === 1 ? 'Published' : 'Draft'}
                        </span>
                      </div>
                      {blog.excerpt && (
                        <p className="text-gray-600 mb-2 line-clamp-2">{blog.excerpt}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-gray-500">
                        <span>
                          {new Date(blog.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                        {blog.tags && blog.tags.length > 0 && (
                          <span className="flex gap-1">
                            {blog.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 bg-gray-100 rounded text-xs"
                              >
                                {tag}
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-row gap-2 lg:ml-4 lg:self-start">
                      <button
                        onClick={() => handlePublishToggle(blog)}
                        className={`px-3 py-1 text-sm font-medium rounded transition ${
                          blog.status === 1
                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {blog.status === 1 ? 'Unpublish' : 'Publish'}
                      </button>
                      <button
                        onClick={() => handleOpenModal(blog)}
                        className="px-3 py-1 text-sm font-medium bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition flex items-center gap-1"
                      >
                        <FaEdit />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(blog._id)}
                        className="px-3 py-1 text-sm font-medium bg-red-100 text-red-700 rounded hover:bg-red-200 transition flex items-center gap-1"
                      >
                        <FaTrash />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm sm:text-base">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex justify-between items-center gap-3">
                <h2 className="text-xl sm:text-2xl font-semibold">
                  {editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
                {error && (
                  <div className="p-3 bg-red-100 text-red-700 rounded-md">{error}</div>
                )}
                {message && (
                  <div className="p-3 bg-green-100 text-green-700 rounded-md">{message}</div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="title">
                    Title *
                  </label>
                  <input
                    id="title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="excerpt">
                    Excerpt
                  </label>
                  <textarea
                    id="excerpt"
                    rows={3}
                    value={excerpt}
                    onChange={(e) => setExcerpt(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Short summary (will auto-generate from content if left empty)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="content">
                    Content *
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    className="bg-white"
                    placeholder="Write your blog content here..."
                    modules={quillModules}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="status">
                      Status
                    </label>
                    <select
                      id="status"
                      value={status}
                      onChange={(e) => setStatus(Number(e.target.value))}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={0}>Draft</option>
                      <option value={1}>Published</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="tags">
                      Tags (comma-separated)
                    </label>
                    <input
                      id="tags"
                      type="text"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. art, painting, contemporary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="featuredImage">
                    Featured Image <span className="text-red-500">*</span>
                  </label>
                  
                  <div className="mb-3">
                    <label className="block text-xs text-gray-600 mb-1">Upload Image</label>
                    <input
                      id="imageFile"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs text-gray-600 mb-1">Or Enter Image URL</label>
                    <input
                      id="featuredImageUrl"
                      type="url"
                      value={imageFile ? '' : featuredImage}
                      onChange={handleImageUrlChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/image.jpg"
                      disabled={!!imageFile}
                    />
                  </div>

                  {imagePreview && (
                    <div className="mt-3">
                      <label className="block text-xs text-gray-600 mb-1">Preview</label>
                      <div className="border border-gray-300 rounded-md overflow-hidden">
                        <img
                          src={imagePreview}
                          alt="Featured image preview"
                          className="w-full h-48 object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            setError('Failed to load image preview')
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Saving...' : editingBlog ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </AdminSectionLayout>
    </>
  )
}

export default AdminBlogPage

